import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Brain, Loader2, RefreshCw } from 'lucide-react';
import { NbaTicketLearning } from '../components/NbaTicketLearning';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/sports-betting/board/review`;
const pct = (x) => (x == null ? '—' : `${Math.round(x * 100)}%`);
const tone = (x) => (x == null ? 'text-zinc-400' : x >= 0.75 ? 'text-emerald-300' : x >= 0.55 ? 'text-amber-300' : 'text-red-400');
const nice = (k) => k.replace(':', ' · ').replace(/_/g, ' ');

const Stat = ({ k, v, id }) => (
  <div className="rounded-lg border border-zinc-800 bg-zinc-900/70 p-2 text-center">
    <p className="text-[10px] uppercase text-zinc-500">{k}</p>
    <p className="text-lg font-bold font-mono text-white" data-testid={id}>{v}</p>
  </div>
);

const wk = (d) => new Date(`${d}T12:00:00`).toLocaleDateString([], { month: 'short', day: 'numeric' });
const Trend = ({ now, prev }) => {
  if (now == null || prev == null || Math.abs(now - prev) < 0.01) return null;
  const up = now > prev;
  return <span className={`ml-1 text-[10px] ${up ? 'text-emerald-300' : 'text-red-400'}`}>{up ? '▲' : '▼'}{Math.abs(Math.round((now - prev) * 100))}</span>;
};

// Week-by-week: are your legs and the app's own picks hitting more since the changes?
const WeeklyProgress = ({ weeks }) => (
  <section className="rounded-xl border border-zinc-800 bg-zinc-900/50 p-3" data-testid="review-weeks">
    <p className="text-sm font-bold">Week by week — is it getting better?</p>
    <p className="text-[11px] text-zinc-500 mb-1">Weeks run Tuesday to Monday night. Arrows compare with the week before.</p>
    {!weeks?.length ? <p className="text-xs text-zinc-500">No finished weeks yet.</p> : (
      <table className="w-full text-xs">
        <thead><tr className="text-zinc-500"><th className="text-left font-normal">Week of</th><th className="font-normal">Tickets W-L</th><th className="font-normal">Lost by 1</th><th className="font-normal">Your legs hit</th><th className="font-normal">App picks hit</th></tr></thead>
        <tbody>{weeks.map((w, i) => {
          const prev = weeks[i + 1];
          return (
            <tr key={w.week} className="border-t border-zinc-800 align-top" data-testid={`review-week-${w.week}`}>
              <td className="py-1">{wk(w.week)}{w.changes?.length > 0 && <span className="block text-[10px] text-fuchsia-300">{w.changes.length} pick change{w.changes.length > 1 ? 's' : ''}</span>}</td>
              <td className="text-center font-mono">{w.won}-{w.lost}</td>
              <td className="text-center font-mono">{w.one_leg_short}</td>
              <td className={`text-center font-mono font-bold ${tone(w.leg_rate)}`}>{pct(w.leg_rate)}<Trend now={w.leg_rate} prev={prev?.leg_rate} /></td>
              <td className={`text-center font-mono font-bold ${tone(w.ai_rate)}`}>{pct(w.ai_rate)}{w.ai_picks ? <span className="text-zinc-500 font-normal"> /{w.ai_picks}</span> : null}<Trend now={w.ai_rate} prev={prev?.ai_rate} /></td>
            </tr>
          );
        })}</tbody>
      </table>
    )}
  </section>
);

// "What went wrong": every leg on the user's tickets graded, what keeps missing, and what the picks changed.
export default function TicketReviewPage() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => axios.get(API).then((r) => setD(r.data)).catch((e) => toast.error(e.message));
  useEffect(() => { load(); }, []);
  const run = async () => {
    setBusy(true);
    try { setD((await axios.post(`${API}/run`)).data); toast.success('Review done — picks updated and emailed to you'); } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setBusy(false);
  };
  const t = d?.tickets || {};
  return (
    <div className="min-h-screen bg-zinc-950 text-white p-4 max-w-3xl mx-auto space-y-4" data-testid="review-page">
      <Link to="/sports-betting" className="inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-white" data-testid="review-back"><ArrowLeft className="w-3.5 h-3.5" />Back to tracker</Link>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-black flex items-center gap-2"><Brain className="w-6 h-6 text-fuchsia-400" />What went wrong</h1>
          <p className="text-xs text-zinc-400">Every leg on your tickets from the last 7 days (deleted and archived included), checked against the final stats. Every Tuesday morning (after the Monday night game) it learns from all past weeks — newest counted most — lowers the line on bet types that keep missing, refreshes the picks and emails you.</p>
        </div>
        <button type="button" onClick={run} disabled={busy} data-testid="review-run"
          className="shrink-0 inline-flex items-center gap-1.5 rounded-lg bg-fuchsia-500 px-3 py-2 text-xs font-bold text-black hover:bg-fuchsia-400 disabled:opacity-50">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}Review & learn now
        </button>
      </div>
      <NbaTicketLearning />
      {!d ? <p className="flex items-center gap-2 text-sm text-zinc-400"><Loader2 className="w-4 h-4 animate-spin" />Loading…</p> : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <Stat k="Won" v={t.won || 0} id="review-won" />
            <Stat k="Lost" v={t.lost || 0} id="review-lost" />
            <Stat k="Lost by 1 leg" v={t.one_leg_short || 0} id="review-one-short" />
            <Stat k="Legs checked" v={t.legs || 0} id="review-legs" />
          </div>
          <section className="rounded-xl border border-fuchsia-500/30 bg-fuchsia-950/20 p-3" data-testid="review-changes">
            <p className="text-sm font-bold text-fuchsia-300">What the picks changed</p>
            {(d.changes || []).length ? <ul className="mt-1 list-disc pl-5 text-xs text-zinc-200 space-y-0.5">{d.changes.map((c) => <li key={c}>{c}</li>)}</ul>
              : <p className="text-xs text-zinc-400 mt-1">{d.preview ? 'Tap “Review & learn now” to apply what this review found.' : 'Nothing needed changing.'}</p>}
          </section>
          <WeeklyProgress weeks={d.weeks} />
          <section data-testid="review-categories">
            <p className="text-sm font-bold mb-1">Hit rate by bet type (worst first)</p>
            <table className="w-full text-xs">
              <thead><tr className="text-zinc-500"><th className="text-left font-normal">Bet type</th><th className="font-normal">Hit</th><th className="font-normal">Rate</th><th className="font-normal">Avg short by</th><th className="font-normal">Close misses</th></tr></thead>
              <tbody>{(d.categories || []).map((r) => (
                <tr key={r.kind} className="border-t border-zinc-800" data-testid={`review-cat-${r.kind}`}>
                  <td className="py-1 capitalize">{nice(r.kind)}</td><td className="text-center font-mono">{r.hit}/{r.legs}</td>
                  <td className={`text-center font-mono font-bold ${tone(r.hit_rate)}`}>{pct(r.hit_rate)}</td>
                  <td className="text-center font-mono">{r.avg_short_by ?? '—'}</td><td className="text-center font-mono">{r.close_misses}</td>
                </tr>))}</tbody>
            </table>
            {!(d.categories || []).length && <p className="text-xs text-zinc-500">No finished tickets in the last 7 days.</p>}
          </section>
          {(d.misses || []).length > 0 && (
            <section data-testid="review-misses">
              <p className="text-sm font-bold mb-1">Legs that missed</p>
              <ul className="space-y-1 text-xs">{d.misses.map((m, i) => (
                <li key={i} className="rounded border border-zinc-800 bg-zinc-900/60 px-2 py-1">
                  <b className="text-white">{m.leg}</b> <span className="text-zinc-500">· {nice(m.kind)}</span>
                  {m.short_by != null && <span className="text-red-300"> · finished {m.actual} vs {m.line} ({m.short_by})</span>}
                  {m.note && <span className="block text-[11px] text-zinc-500">{m.note}</span>}
                </li>))}</ul>
            </section>
          )}
        </>
      )}
    </div>
  );
}
